/**
 * Tenant-facing AMC / maintenance notice — driven by real unpaid AMC invoices
 * plus SaaS Admin notice config (billing.amc_notice).
 */
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { AlertTriangle, X } from "lucide-react";
import { dataProvider as supabase } from "@/lib/providers/data-provider";
import { fetchTenantInvoices } from "@/lib/platform-payments/platform-payment-service";
import { cn } from "@/lib/utils";

type AmcNoticeConfig = {
  enabled: boolean;
  show_banner: boolean;
  show_popup: boolean;
  severity: "info" | "warning" | "danger";
  title: string;
  body: string;
  cta_label: string;
  cta_path: string;
};

const SEVERITY_STYLES = {
  info: "border-blue-500/30 bg-blue-500/10 text-blue-800 dark:text-blue-200",
  warning: "border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-100",
  danger: "border-red-500/30 bg-red-500/10 text-red-900 dark:text-red-100",
};

export function PlatformBillingNoticeBanner() {
  const [config, setConfig] = useState<AmcNoticeConfig | null>(null);
  const [hasUnpaidAmc, setHasUnpaidAmc] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);

  useEffect(() => {
    void (async () => {
      const { data } = await supabase
        .from("platform_settings" as never)
        .select("value")
        .eq("key", "billing.amc_notice")
        .maybeSingle();
      if (data && typeof (data as { value: unknown }).value === "object") {
        setConfig((data as { value: AmcNoticeConfig }).value);
      }
      const invoices = await fetchTenantInvoices();
      const unpaidAmc = invoices.some(
        (i) =>
          i.itemType?.toLowerCase() === "amc" &&
          i.balancePaise > 0 &&
          ["sent", "overdue", "partially_paid"].includes(i.status.toLowerCase()),
      );
      setHasUnpaidAmc(unpaidAmc);
    })();
  }, []);

  if (!config?.enabled || !hasUnpaidAmc) return null;

  const showBanner = config.show_banner && !dismissed;
  const showPopup = config.show_popup && !popupDismissed;

  return (
    <>
      {showBanner ? (
        <div
          className={cn(
            "flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b text-sm shrink-0",
            SEVERITY_STYLES[config.severity] ?? SEVERITY_STYLES.warning,
          )}
          data-testid="amc-notice-banner"
        >
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <div className="min-w-0">
              <span className="font-semibold">{config.title}</span>
              <span className="text-xs ml-2 opacity-90">{config.body}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button size="sm" variant="outline" className="h-7 text-xs" asChild>
              <Link to={config.cta_path}>{config.cta_label}</Link>
            </Button>
            <button
              type="button"
              className="p-1 rounded hover:bg-black/5"
              aria-label="Dismiss banner"
              onClick={() => setDismissed(true)}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : null}

      {showPopup ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
          <div className="max-w-md w-full rounded-xl border border-border bg-card p-5 shadow-xl space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold">{config.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{config.body}</p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setPopupDismissed(true)}>
                Later
              </Button>
              <Button size="sm" asChild>
                <Link to={config.cta_path} onClick={() => setPopupDismissed(true)}>
                  {config.cta_label}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
